import React, { useMemo, useCallback } from 'react';
import { useStatusDelete, useStatusGetFilters, useStatusList } from '../hooks/use-status.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Status } from '../types/status.types';
import StatusTable from '../components/status-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';

const StatusTableContainer = () => {
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } = useStatusGetFilters();

  const { data, isLoading, isFetching } = useStatusList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteStatusMutation = useStatusDelete();

  const handleDeleteStatus = useCallback(async (record: Status) => {
    try {
      await deleteStatusMutation.mutateAsync(record.id);
      message.success(t('crud.delete.success', { entity: t('statuses.title') }));
    } catch (error) {
      const errorMessage = extractCreateError(
        error,
        t('crud.delete.error', { entity: t('statuses.title') })
      );
      message.error(errorMessage);
    }
  }, []);

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<Status>[]>(
    () => [
      {
        title: t('statuses.candidateStep.label'),
        key: 'candidateStep',
        dataIndex: 'candidateStep',
        sorter: true,
      },
      {
        title: t('statuses.statusCode.label'),
        key: 'statusCode',
        dataIndex: 'statusCode',
        sorter: true,
      },
      {
        title: t('statuses.statusOutcome.label'),
        key: 'statusOutcome',
        dataIndex: 'statusOutcome',
        sorter: true,
      },
      {
        title: t('statuses.stageDescription.label'),
        key: 'stageDescription',
        dataIndex: 'stageDescription',
        sorter: true,
      },
      {
        title: t('statuses.actionBy.label'),
        key: 'actionBy',
        dataIndex: 'actionBy',
        sorter: true,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_: any, record: Status) => (
          <ActionButtons
            showEdit={rbac.hasAccess(userPermissions, 'status.update')}
            showDelete={rbac.hasAccess(userPermissions, 'status.delete')}
            showView={rbac.hasAccess(userPermissions, 'status.view')}
            record={record}
            baseUrl="/dashboard/status"
            onDelete={handleDeleteStatus}
            isDeleting={
              deleteStatusMutation.isPending && deleteStatusMutation.variables === record.id
            }
          />
        ),
      },
    ],
    [
      handleDeleteStatus,
      deleteStatusMutation.isPending,
      deleteStatusMutation.variables,
      t,
      userPermissions,
    ]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('statuses.title') })}
        createRoute="create"
        showCreateButton={rbac.hasAccess(userPermissions, 'status.create')}
      />
      <div className="row mt-3">
        <StatusTable
          data={tableData}
          columns={columns}
          loading={
            isLoading || deleteStatusMutation.isPending || isFetching || isAvailableFiltersLoading
          }
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(StatusTableContainer, 'status.view');
